import React from 'react';
import styled from 'styled-components';
import { config } from '../../config';

const PolicyContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px 40px;
  
  @media (max-width: 768px) {
    padding: 60px 20px 40px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  margin-bottom: 32px;
  font-size: 14px;
`;

const Section = styled.section`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #333;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0 16px 24px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 8px;
  }
`;

const PrivacyPolicy = () => {
  return (
    <PolicyContainer>
      <Title>Privacy Policy</Title>
      <LastUpdated>Last Updated: December 28, 2024</LastUpdated>

      <Section>
        <Paragraph>
          {config.company.name} ("we," "our," or "us") is committed to protecting your privacy. 
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
          when you visit our website and use our services.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>1. Information We Collect</SectionTitle>
        <Paragraph>We collect information that you provide directly to us, including:</Paragraph>
        <List>
          <li><strong>Personal Information:</strong> Name, email address, phone number, and postal address</li>
          <li><strong>Payment Information:</strong> Payment details processed securely through PhonePe</li>
          <li><strong>Account Information:</strong> Username, password, and preferences</li>
          <li><strong>Communication Data:</strong> Records of your correspondence with us</li>
          <li><strong>Usage Data:</strong> Information about how you use our website and services</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>2. How We Use Your Information</SectionTitle>
        <Paragraph>We use the collected information for:</Paragraph>
        <List>
          <li>Providing, maintaining, and improving our services</li>
          <li>Processing payments and transactions</li>
          <li>Sending you technical notices, updates, and support messages</li>
          <li>Responding to your comments and questions</li>
          <li>Sending marketing and promotional communications (with your consent)</li>
          <li>Monitoring and analyzing trends, usage, and activities</li>
          <li>Detecting and preventing fraud and abuse</li>
          <li>Complying with legal obligations</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>3. Payment Processing</SectionTitle>
        <Paragraph>
          All payment transactions are processed through PhonePe, a secure payment gateway. 
          We do not store your complete credit/debit card information. Payment information is 
          encrypted and transmitted securely to PhonePe for processing.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>4. Information Sharing and Disclosure</SectionTitle>
        <Paragraph>We may share your information in the following circumstances:</Paragraph>
        <List>
          <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
          <li><strong>Payment Processors:</strong> With PhonePe for payment processing</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>5. Data Security</SectionTitle>
        <Paragraph>
          We implement appropriate technical and organizational measures to protect your personal 
          information against unauthorized access, alteration, disclosure, or destruction. However, 
          no method of transmission over the Internet is 100% secure.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Your Rights</SectionTitle>
        <Paragraph>You have the right to:</Paragraph>
        <List>
          <li>Access, update, or delete your personal information</li>
          <li>Opt-out of marketing communications</li>
          <li>Request a copy of your data</li>
          <li>Withdraw consent for data processing</li>
          <li>Lodge a complaint with a supervisory authority</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>7. Cookies and Tracking</SectionTitle>
        <Paragraph>
          We use cookies and similar tracking technologies to track activity on our website and 
          store certain information. You can instruct your browser to refuse all cookies or to 
          indicate when a cookie is being sent.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Children's Privacy</SectionTitle>
        <Paragraph>
          Our services are not intended for individuals under the age of 18. We do not knowingly 
          collect personal information from children under 18.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Changes to This Privacy Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time. We will notify you of any changes 
          by posting the new Privacy Policy on this page and updating the "Last Updated" date.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about this Privacy Policy, please contact us:
        </Paragraph>
        <List>
          <li><strong>Email:</strong> {config.company.email}</li>
          <li><strong>Phone:</strong> {config.company.phone}</li>
          <li><strong>Address:</strong> {config.company.address}</li>
        </List>
      </Section>
    </PolicyContainer>
  );
};

export default PrivacyPolicy;
